import React, { useEffect, useMemo, useRef, useState } from "react";
import html2pdf from "html2pdf.js"; // npm i html2pdf.js
import { PDFDocument } from "pdf-lib"; // NEW: npm i pdf-lib
import PatientReport from "./PatientReport"; // PatientReport.jsx in the same folder

const BASE_URL = "https://cbackend.xraidigital.com/api";
const LOGO_SRC = "/campanylogo.jpeg"; // file must be in your public/ folder

// Brand colour: #11a8a4 (hover: #0e8683)
// Token authentication: header format is  Authorization: Token <key>
// Change TOKEN_KEYS if you saved the token in localStorage under another name.
const TOKEN_KEYS = ["token", "authToken", "access_token"];

const getToken = () => {
  try {
    for (const k of TOKEN_KEYS) {
      const t = localStorage.getItem(k);
      if (t) return t.replace(/^"|"$/g, ""); // strip quotes if stored via JSON.stringify
    }
  } catch (e) {}
  return null;
};

const authHeaders = () => {
  const token = getToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Token ${token}` } : {}),
  };
};

const getJSON = async (path) => {
  if (!getToken()) throw new Error("You are not logged in. Please log in again.");
  const res = await fetch(`${BASE_URL}${path}`, { headers: authHeaders() });
  if (res.status === 401 || res.status === 403)
    throw new Error("Session expired or not allowed. Please log in again.");
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
};

const show = (v, unit = "") =>
  v === null || v === undefined || v === "" ? "—" : `${v}${unit ? " " + unit : ""}`;

const bmiCategory = (bmi) => {
  if (bmi == null) return "—";
  if (bmi < 18.5) return "Underweight";
  if (bmi < 25) return "Normal";
  if (bmi < 30) return "Overweight";
  return "Obese";
};

// PDF file name: <patient id>_<patient name>.pdf  e.g. 236477_Rohan_Jangid.pdf
// (change p.id to p.unique_patient_id if you want the unique ID instead)
const makePdfFileName = (p) => {
  const name =
    String(p.patient_name || "patient")
      .trim()
      .replace(/[^\p{L}\p{N}]+/gu, "_")
      .replace(/^_+|_+$/g, "") || "patient";
  return `${p.id}_${name}.pdf`;
};

// Indian mobile numbers: keep digits, add 91 if only 10 digits
const toWhatsAppNumber = (num) => {
  const digits = String(num || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  return digits;
};

// Poll until fn() returns something truthy (or time runs out)
const waitFor = async (fn, timeout = 20000, step = 150) => {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const value = fn();
    if (value) return value;
    await new Promise((r) => setTimeout(r, step));
  }
  return null;
};

/* ---------- X-ray report helpers ---------- */
const A4_W = 595.28; // A4 in PDF points
const A4_H = 841.89;

// Calls the Camp backend only (it fetches from DICOM and returns the presigned URL)
const fetchXrayReport = (campId, patientExcelId) => {
  if (!patientExcelId) throw new Error("Patient has no patient_excel_id.");
  return getJSON(
    `/campmanager/camp/${campId}/patients/${encodeURIComponent(patientExcelId)}/xray-report/`
  );
};

// Turns the API response into one of: pending | processing | completed | error
const classifyXray = (res) => {
  if (!res || res.success !== true)
    return { state: "error", message: res?.message || "X-ray status check failed." };
  if (res.found !== true || !res.report)
    return { state: "pending", message: res.message || "X-ray report not available yet." };
  if (res.report.isDone !== true)
    return { state: "processing", message: "X-ray report is still being prepared." };
  if (!res.report.report_file)
    return { state: "error", message: "X-ray is marked done but the report file is missing." };
  return { state: "completed", reportFile: res.report.report_file };
};

// Run worker(item) for all items, at most `limit` at a time
const runPool = async (items, limit, worker) => {
  let i = 0;
  await Promise.allSettled(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (i < items.length) await worker(items[i++]);
    })
  );
};

// Find "%PDF-" in the first 1 KB (the PDF spec allows junk bytes before it)
const findPdfStart = (bytes) => {
  const limit = Math.min(bytes.length - 5, 1024);
  for (let i = 0; i <= limit; i++) {
    if (
      bytes[i] === 0x25 && bytes[i + 1] === 0x50 && bytes[i + 2] === 0x44 &&
      bytes[i + 3] === 0x46 && bytes[i + 4] === 0x2d
    ) return i;
  }
  return -1;
};

// Downloads the X-ray PDF through the Camp backend (no S3 CORS, uses the Camp token)
const downloadXrayPdfViaBackend = async (campId, patientExcelId) => {
  const token = getToken();
  if (!token) throw new Error("You are not logged in. Please log in again.");

  let res;
  try {
    res = await fetch(
      `${BASE_URL}/campmanager/camp/${campId}/patients/${encodeURIComponent(patientExcelId)}/xray-report/download/`,
      { headers: { Authorization: `Token ${token}` }, cache: "no-store" }
    );
  } catch {
    throw new Error("Could not reach the server to download the X-ray PDF.");
  }

  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.message || `X-ray PDF download failed (${res.status}).`);
  }

  const buf = await res.arrayBuffer();
  const start = findPdfStart(new Uint8Array(buf));
  if (start < 0) throw new Error("The server did not return a valid X-ray PDF.");
  return start === 0 ? buf : buf.slice(start);
};

// Page 1-2 = health report (untouched), then every X-ray page scaled to fit A4
const mergeXrayIntoReport = async (reportBlob, xrayBuf) => {
  let merged, xrayDoc;
  try {
    merged = await PDFDocument.load(await reportBlob.arrayBuffer());
  } catch {
    throw new Error("The generated health report PDF could not be read for merging.");
  }
  try {
    xrayDoc = await PDFDocument.load(xrayBuf);
  } catch {
    throw new Error("The X-ray PDF is corrupted or password-protected.");
  }
  if (xrayDoc.getPageCount() === 0) throw new Error("The X-ray PDF has no pages.");

  const embedded = await merged.embedPdf(xrayDoc, xrayDoc.getPageIndices());
  embedded.forEach((ep) => {
    const scale = Math.min(A4_W / ep.width, A4_H / ep.height);
    const w = ep.width * scale;
    const h = ep.height * scale;
    const page = merged.addPage([A4_W, A4_H]);
    page.drawPage(ep, { x: (A4_W - w) / 2, y: (A4_H - h) / 2, width: w, height: h });
  });
  const bytes = await merged.save();
  return new Blob([bytes], { type: "application/pdf" });
};

const XRAY_BADGE = {
  loading: { label: "Checking…", cls: "bg-slate-100 text-slate-600" },
  completed: { label: "X-Ray Completed", cls: "bg-[#11a8a4]/15 text-[#0e8683]" },
  processing: { label: "X-Ray Processing", cls: "bg-amber-100 text-amber-800" },
  pending: { label: "X-Ray Pending", cls: "bg-slate-200 text-slate-700" },
  error: { label: "X-Ray Status Error", cls: "bg-red-100 text-red-800" },
};

// ---------- Report status helpers (data comes from the camp patients API) ----------
const prettify = (v) =>
  String(v || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return isNaN(d)
    ? "—"
    : d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

// Newest report first (API already sends newest first, but sort to be safe)
const latestReport = (p) =>
  [...(p.reviewer_reports || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0] || null;

// API returns http:// media links; use https:// when this page is on https
const secureUrl = (u) =>
  typeof window !== "undefined" && window.location.protocol === "https:" ? String(u).replace(/^http:\/\//, "https://") : u;

// Infectious-disease results (HIV / HCV / HBsAg) are deliberately left out of the
// WhatsApp text. Share those only in person or through a secure channel.
const buildWhatsAppMessage = (data) => {
  const { patient, vitals: v, consultation: c, services } = data;
  const done = (services || []).filter((s) => s.is_completed).map((s) => s.service_name);
  const lines = [
    `Hello ${patient.patient_name},`,
    `Here is your health camp summary.`,
    ``,
    `Age / Gender: ${patient.age} / ${patient.gender}`,
    `Height: ${show(v.height, "cm")}  Weight: ${show(v.weight, "kg")}`,
    `BMI: ${show(v.bmi)} (${bmiCategory(v.bmi)})`,
    `Hemoglobin: ${show(v.hemoglobin, "g/dL")}`,
  ];
  if (c?.advice) lines.push(``, `Doctor's advice: ${c.advice}`);
  if (done.length) lines.push(``, `Services completed: ${done.join(", ")}`);
  lines.push(``, `Please contact us if you have any questions.`);
  return lines.join("\n");
};

// Label shown for a camp in the dropdown / input
const campLabel = (c) => `${c.branch_name} – ${c.location} (${c.start_date})`;

/* Searchable dropdown: click to open, type to filter, arrow keys + Enter to pick, Esc to close */
function CampSelect({ camps, value, onChange, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const selected = camps.find((c) => String(c.id) === String(value));

  const options = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return camps;
    return camps.filter((c) =>
      [c.branch_name, c.location, c.district, c.state, c.client_name, c.client, c.branch_code, c.pin_code, c.start_date]
        .some((f) => String(f ?? "").toLowerCase().includes(q))
    );
  }, [camps, query]);

  // Close when clicking outside
  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Reset highlighted row when the list changes
  useEffect(() => setActive(0), [query, open]);

  // Keep the highlighted row visible while using arrow keys
  useEffect(() => {
    if (open) listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (camp) => {
    onChange(camp ? String(camp.id) : "");
    setOpen(false);
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (options[active]) choose(options[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <label htmlFor="camp-search" className="block text-sm font-medium text-slate-700">
        Camp
      </label>
      <div className="relative mt-1">
        <input
          id="camp-search"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls="camp-listbox"
          aria-autocomplete="list"
          aria-activedescendant={open && options[active] ? `camp-opt-${options[active].id}` : undefined}
          autoComplete="off"
          disabled={disabled}
          value={open ? query : selected ? campLabel(selected) : ""}
          placeholder={loading ? "Loading camps…" : "Search or select a camp"}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className="block w-full rounded border border-slate-300 bg-white py-2 pl-3 pr-16 text-slate-900 focus:border-[#11a8a4] focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 disabled:bg-slate-100"
        />
        <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
          {selected && !disabled && (
            <button
              type="button"
              aria-label="Clear selected camp"
              onClick={() => choose(null)}
              className="rounded px-1.5 text-lg leading-none text-slate-400 hover:text-slate-700"
            >
              ×
            </button>
          )}
          <span aria-hidden="true" className="pointer-events-none text-xs text-slate-400">▼</span>
        </div>
      </div>

      {open && !disabled && (
        <ul
          id="camp-listbox"
          role="listbox"
          ref={listRef}
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded border border-slate-200 bg-white shadow-lg"
        >
          {options.length === 0 && (
            <li className="px-3 py-3 text-sm text-slate-500">No camps match “{query}”.</li>
          )}
          {options.map((c, i) => (
            <li
              key={c.id}
              id={`camp-opt-${c.id}`}
              role="option"
              aria-selected={String(c.id) === String(value)}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(c)}
              className={`cursor-pointer px-3 py-2 text-sm ${
                i === active ? "bg-[#11a8a4]/10" : ""
              } ${String(c.id) === String(value) ? "font-semibold text-[#0e8683]" : "text-slate-900"}`}
            >
              <div>{campLabel(c)}</div>
              <div className="text-xs font-normal text-slate-500">
                {[c.district, c.state, c.client_name].filter(Boolean).join(" · ")}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const ReviewerPage = () => {
  const [camps, setCamps] = useState([]);
  const [campId, setCampId] = useState("");
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");

  const [loadingCamps, setLoadingCamps] = useState(true);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  // Which row is busy and doing what: { id, type: "report" | "whatsapp" }
  const [busy, setBusy] = useState(null);
  // Patient whose report is being rendered off-screen for the PDF
  const [renderPatient, setRenderPatient] = useState(null);
  // NEW: X-ray status per patient row id -> { state, reportFile?, message? }
  const [xrayStatus, setXrayStatus] = useState({});
  const [loadingXray, setLoadingXray] = useState(false);
  const xrayReqRef = useRef(0);        // incremented for every load; used to ignore stale responses
  const activeCampRef = useRef("");    // always holds the currently selected camp
  activeCampRef.current = String(campId);

  const hiddenRef = useRef(null);

  // Stable headers object for PatientReport (re-read for each report)
  const reportHeaders = useMemo(() => {
    const token = getToken();
    return token ? { Authorization: `Token ${token}` } : {};
  }, [renderPatient]);

  // 1. Load camps
  useEffect(() => {
    getJSON("/camps/")
      .then((data) => setCamps(Array.isArray(data) ? data : data.results || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingCamps(false));
  }, []);

  // NEW: check X-ray status for every patient of the camp
  const loadXrayStatuses = async (cid, list) => {
    const reqId = ++xrayReqRef.current;
    const isStale = () =>
      reqId !== xrayReqRef.current || activeCampRef.current !== String(cid);

    setLoadingXray(true);
    setXrayStatus(Object.fromEntries(list.map((p) => [p.id, { state: "loading" }])));

    try {
      await runPool(list, 5, async (p) => {
        let result;
        try {
          result = classifyXray(await fetchXrayReport(cid, p.patient_excel_id));
        } catch (e) {
          result = { state: "error", message: e.message };
        }
        if (isStale()) return; // camp changed or a newer load started
        setXrayStatus((prev) => ({ ...prev, [p.id]: result }));
      });
    } finally {
      if (!isStale()) setLoadingXray(false);
    }
  };

  // CHANGED: ignores stale camp responses and refreshes X-ray status
  const fetchPatients = async (id, silent = false, refreshXray = true) => {
    if (!silent) setLoadingPatients(true);
    try {
      const data = await getJSON(`/technician/camps/${id}/patients/`);
      if (activeCampRef.current !== String(id)) return; // camp changed meanwhile
      const list = data.patients || [];
      setPatients(list);
      if (refreshXray) loadXrayStatuses(id, list); // not awaited: table shows immediately
    } finally {
      if (!silent) setLoadingPatients(false);
    }
  };

  useEffect(() => {
    xrayReqRef.current += 1; // invalidate any in-flight X-ray requests of the old camp
    setXrayStatus({});
    setLoadingXray(false);
    if (!campId) {
      setPatients([]);
      return;
    }
    setError("");
    fetchPatients(campId).catch((e) => setError(e.message));
  }, [campId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(
      (p) =>
        p.patient_name?.toLowerCase().includes(q) ||
        p.unique_patient_id?.toLowerCase().includes(q) ||
        p.contact_number?.includes(q)
    );
  }, [patients, search]);

  const selectedCamp = camps.find((c) => String(c.id) === String(campId));

  /* ---------- Generate report (runs straight from the list row) ---------- */
  const handleGenerateReport = async (p) => {
    if (busy) return;
    const token = getToken();
    if (!token) {
      setError("Authentication token not found. Please log in again.");
      return;
    }
    if (!campId) {
      setError("Please select a camp first.");
      return;
    }

    // NEW: X-ray must be completed before anything starts
    const xr = xrayStatus[p.id];
    if (xr?.state !== "completed" || !xr.reportFile) {
      setError(`X-ray report for ${p.patient_name} is not completed yet. Report generation is disabled.`);
      return;
    }

    setBusy({ id: p.id, type: "report" });
    setError("");
    setNotice("");

    const pdfFileName = makePdfFileName(p);

    try {
      // NEW 0. Re-check the X-ray (the presigned URL from the list may have expired)
      //        and download the X-ray PDF BEFORE the heavy rendering work.
      const fresh = classifyXray(await fetchXrayReport(campId, p.patient_excel_id));
      if (fresh.state !== "completed") {
        throw new Error(fresh.message || "X-ray report is not completed.");
      }
      const xrayBuf = await downloadXrayPdfViaBackend(campId, p.patient_excel_id);

      // 1. Render the report off-screen (it loads its own data)
      setRenderPatient(p);

      const found = await waitFor(
        () =>
          hiddenRef.current?.querySelector(".xr-sheets") ||
          hiddenRef.current?.querySelector(".xr-error")
      );
      if (!found) throw new Error("Timed out while loading the report data.");
      if (found.classList.contains("xr-error")) {
        throw new Error(found.textContent || "The report data could not be loaded.");
      }
      const sheetsEl = found;

      // 2. Wait for the logo (and any images) to finish loading
      await Promise.all(
        Array.from(sheetsEl.querySelectorAll("img")).map((img) =>
          img.complete
            ? null
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              })
        )
      );

      // 3. Make each sheet fill exactly one A4 page (no gaps / shadow)
      sheetsEl.querySelectorAll(".xr-sheet").forEach((s) => {
        s.style.margin = "0";
        s.style.boxShadow = "none";
        s.style.height = "296.5mm";
      });

      // 4. Build the 2-page health report PDF
      const reportBlob = await html2pdf() // CHANGED: renamed from pdfBlob
        .set({
          margin: 0,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
          pagebreak: { mode: ["css"] },
        })
        .from(sheetsEl)
        .outputPdf("blob");

      // NEW 4b. Append the X-ray PDF pages after page 2 (throws -> nothing is uploaded)
      const pdfBlob = await mergeXrayIntoReport(reportBlob, xrayBuf);

      // 5. Save the MERGED pdf to the backend (unchanged contract)
      const formData = new FormData();
      formData.append("report_file", pdfBlob, pdfFileName);
      formData.append("camp_id", String(campId));
      formData.append("patient_id", String(p.id));
      formData.append("patient_unique_id", String(p.unique_patient_id || ""));
      formData.append("report_title", `Health Report - ${p.patient_name || "Patient"}`);

      const response = await fetch(`${BASE_URL}/technician/reports/generate/`, {
        method: "POST",
        headers: { Authorization: `Token ${token}` },
        body: formData,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.success === false) {
        throw new Error(result.message || result.detail || `Request failed (${response.status})`);
      }
      setNotice(`Report saved for ${p.patient_name}: ${pdfFileName}`);
      // CHANGED: refresh report status only; no need to re-check every X-ray
      fetchPatients(campId, true, false).catch(() => {});

      // 6. Download the merged PDF (same filename logic)
      const downloadUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = pdfFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    } catch (err) {
      setError(`Could not generate report for ${p.patient_name}: ${err.message}`);
    } finally {
      setRenderPatient(null);
      setBusy(null);
    }
  };

  /* ---------- Send WhatsApp ---------- */
  const handleSendWhatsApp = async (p) => {
    if (busy) return;
    const number = toWhatsAppNumber(p.contact_number);
    if (number.length < 11) {
      setError(`${p.patient_name} has no valid contact number.`);
      return;
    }
    setBusy({ id: p.id, type: "whatsapp" });
    setError("");
    setNotice("");
    try {
      const data = await getJSON(`/technician/patients/${p.id}/report-data/`);
      const text = encodeURIComponent(buildWhatsAppMessage(data));
      window.open(`https://wa.me/${number}?text=${text}`, "_blank", "noopener");
    } catch (e) {
      setError(`Could not send WhatsApp to ${p.patient_name}: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6">
      <header className="mb-6">
        <button
          type="button"
          onClick={() => window.history.back()}
          disabled={!!busy}
          className="mb-3 inline-flex items-center gap-1 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span aria-hidden="true">←</span> Back
        </button>
        <h1 className="text-2xl font-bold text-[#0e8683]">Reviewer Dashboard</h1>
        <p className="mt-1 text-sm text-slate-600">
          Pick a camp, then generate a report or send it to the patient on WhatsApp.
        </p>
      </header>

      {error && (
        <div
          role="alert"
          className="mb-4 flex items-start justify-between rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          <span>{error}</span>
          <button onClick={() => setError("")} className="ml-4 font-medium underline">
            Dismiss
          </button>
        </div>
      )}

      {notice && (
        <div
          role="status"
          className="mb-4 flex items-start justify-between rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800"
        >
          <span>{notice}</span>
          <button onClick={() => setNotice("")} className="ml-4 font-medium underline">
            Dismiss
          </button>
        </div>
      )}

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <CampSelect
          camps={camps}
          value={campId}
          loading={loadingCamps}
          disabled={loadingCamps || !!busy}
          onChange={(id) => {
            setCampId(id);
            setSearch("");
          }}
        />

        <label className="block text-sm font-medium text-slate-700">
          Search patients
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name, patient ID or phone"
            disabled={!campId}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-slate-900 focus:border-[#11a8a4] focus:outline-none focus:ring-2 focus:ring-[#11a8a4]/40 disabled:bg-slate-100"
          />
        </label>
      </div>

      {selectedCamp && (
        <p className="mb-3 text-sm text-slate-600">
          {selectedCamp.branch_name}, {selectedCamp.district}, {selectedCamp.state} ·{" "}
          {selectedCamp.start_date} to {selectedCamp.end_date} ·{" "}
          <strong>{patients.length}</strong> patient{patients.length === 1 ? "" : "s"} ·{" "}
          <strong>{patients.filter((p) => p.has_report).length}</strong> with a report
        </p>
      )}

      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#11a8a4]/10 text-slate-800">
            <tr>
              <th className="px-4 py-3 font-semibold">Patient</th>
              <th className="px-4 py-3 font-semibold">Age / Gender</th>
              <th className="px-4 py-3 font-semibold">Phone</th>
              <th className="px-4 py-3 font-semibold">Package</th>
              <th className="px-4 py-3 font-semibold">Test date</th>
              <th className="px-4 py-3 font-semibold">Checked in</th>
              <th className="px-4 py-3 font-semibold">
                X-ray status
                {loadingXray && <span className="ml-2 text-xs font-normal text-slate-500">(checking…)</span>}
              </th>
              <th className="px-4 py-3 font-semibold">Report status</th>
              <th className="px-4 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {!campId && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                  Select a camp to see its patients.
                </td>
              </tr>
            )}
            {campId && loadingPatients && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                  Loading patients…
                </td>
              </tr>
            )}
            {campId && !loadingPatients && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                  No patients found.
                </td>
              </tr>
            )}
            {filtered.map((p) => {
              const rowBusy = busy?.id === p.id ? busy.type : null;
              const latest = latestReport(p);
              const xr = xrayStatus[p.id]; // NEW
              const xrBadge = XRAY_BADGE[xr?.state || "loading"]; // NEW
              const xrayReady = xr?.state === "completed" && !!xr.reportFile; // NEW
              return (
                <tr key={p.id} className="hover:bg-[#11a8a4]/5">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.patient_name}</div>
                    <div className="text-xs text-slate-500">{p.unique_patient_id}</div>
                  </td>
                  <td className="px-4 py-3">
                    {p.age} / {p.gender}
                  </td>
                  <td className="px-4 py-3">{show(p.contact_number)}</td>
                  <td className="px-4 py-3">{show(p.package_name)}</td>
                  <td className="px-4 py-3">{show(p.test_date)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-0.5 text-xs font-medium ${
                        p.checked_in ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      {p.checked_in ? "Yes" : "No"}
                    </span>
                  </td>

                  {/* NEW: X-ray status cell (separate from reviewer report status) */}
                  <td className="px-4 py-3">
                    <span
                      title={xr?.message || xrBadge.label}
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${xrBadge.cls}`}
                    >
                      {(!xr || xr.state === "loading") && (
                        <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-400 border-t-transparent" />
                      )}
                      {xrBadge.label}
                    </span>
                  </td>

                  {/* Existing Report status cell (unchanged) */}
                  <td className="px-4 py-3">
                    {p.has_report ? (
                      <div className="space-y-1">
                        <span className="inline-block rounded bg-[#11a8a4]/15 px-2 py-0.5 text-xs font-medium text-[#0e8683]">
                          {prettify(latest?.status || "generated")}
                          {(p.report_count ?? p.reviewer_reports?.length ?? 0) > 1
                            ? ` · ${p.report_count ?? p.reviewer_reports.length} reports`
                            : ""}
                        </span>
                        {latest && (
                          <>
                            <div className="text-xs text-slate-500">{formatDateTime(latest.created_at)}</div>
                            <div className="flex flex-wrap items-center gap-x-2 text-xs">
                              {latest.report_file && (
                                <a
                                  href={secureUrl(latest.report_file)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-[#0e8683] underline"
                                >
                                  View PDF
                                </a>
                              )}
                              {latest.reviewed_at && (
                                <span className="text-green-700">Reviewed {formatDateTime(latest.reviewed_at)}</span>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">
                        Not generated
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleGenerateReport(p)}
                        disabled={!!busy || !xrayReady}
                        title={
                          xrayReady
                            ? "Generate the health report with the X-ray attached"
                            : xr?.message || "Waiting for X-ray report to be completed"
                        }
                        className="rounded bg-[#11a8a4] px-3 py-1.5 text-white hover:bg-[#0e8683] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {rowBusy === "report" ? "Generating…" : p.has_report ? "Regenerate report" : "Generate report"}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendWhatsApp(p)}
                        disabled={!!busy}
                        className="rounded bg-green-600 px-3 py-1.5 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {rowBusy === "whatsapp" ? "Opening…" : "Send WhatsApp"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Off-screen copy of the report, used only to build the PDF. Not visible to the user. */}
      {renderPatient && (
        <div
          ref={hiddenRef}
          aria-hidden="true"
          style={{ position: "fixed", left: "-10000px", top: 0, width: "210mm", pointerEvents: "none" }}
        >
          <PatientReport
            key={renderPatient.id}
            patientId={renderPatient.id}
            headers={reportHeaders}
            logoSrc={LOGO_SRC}
            campName={selectedCamp ? `${selectedCamp.branch_name} Camp` : "Health Camp"}
            autoLoad
            hideControls
          />
        </div>
      )}
    </div>
  );
};

export default ReviewerPage;